import type { IconType } from "react-icons";
import { FaBolt, FaHandshake, FaShieldAlt } from "react-icons/fa";
import { SiVuedotjs, SiHtml5, SiReact } from "react-icons/si";

/* -------------------------------------------------------------------------- */
/*                                 TRUSTED BY                                 */
/* -------------------------------------------------------------------------- */

export const trustedLogos = [
  { name: "The CaseVilla", src: "/images/logo/casevilla.png" },
  { name: "Colorbar", src: "/images/logo/colorbar.png" },
  { name: "Health & Wellness Shop", src: "/images/logo/health.png" },
  { name: "Necked", src: "/images/logo/necked.png" },
  { name: "Tarohi", src: "/images/logo/tarohi.png" },
  { name: "Toyzhub", src: "/images/logo/toyzhub.png" },
  { name: "Vastralipi", src: "/images/logo/vastralipi_logo.png" },
  { name: "Suruchira", src: "/images/logo/suruchira.png" },
  { name: "Jewelled by Vandana", src: "/images/logo/Jewelled-by-vandana.png" },
];

/* -------------------------------------------------------------------------- */
/*                          SIMPLIFY ORDER MANAGEMENT                         */
/* -------------------------------------------------------------------------- */

export type SimplePoint = {
  title: string;
  Icon: IconType;
};

export const simplifyPoints: SimplePoint[] = [
  { title: "Fast & User-Friendly Interface", Icon: FaBolt },
  { title: "Manage Brokerage", Icon: FaHandshake },
  { title: "Secure Data Protection", Icon: FaShieldAlt },
];

/* -------------------------------------------------------------------------- */
/*                             GETTING STARTED                                */
/* -------------------------------------------------------------------------- */

export type GettingStartedStep = {
  step: string;
  title: string;
  description: string;
  /** Illustration panel artwork — drop the files into /public/images/brokerage/ */
  image: string;
  alt: string;
};

export const gettingStartedSteps = [
  {
    step: "01",
    title: "Register with WhatsApp",
    description:
      "Sign up in seconds using your WhatsApp account — fast and secure.",
    image: "/images/brokerage/register-whatsapp.png",
    panel: "linear-gradient(180deg,#d9b0ea 0%,#fcdfe6 100%)",
  },
  {
    step: "02",
    title: "Build Your Company Profile",
    description:
      "Add your business information to showcase your brand professionally.",
    image: "/images/brokerage/company-profile.png",
    panel: "linear-gradient(180deg,#fce0e6 0%,#d5a8e8 100%)",
  },
  {
    step: "03",
    title: "List Your Products",
    description:
      "Display your offerings to attract more customers and increase sales.",
    image: "/images/brokerage/product-list.png",
    panel: "linear-gradient(180deg,#d9b0ea 0%,#fcdfe6 100%)",
  },
  {
    step: "04",
    title: "Invite Customers",
    description: "Easily reach out to your audience and keep them engaged.",
    image: "/images/brokerage/invite-customers.png",
    panel: "linear-gradient(180deg,#fce0e6 0%,#d5a8e8 100%)",
  },
  {
    step: "05",
    title: "Manage Brokerage",
    description:
      "Streamline your brokerage operations with smart tools for tracking, managing, and optimizing deals efficiently.",
    image: "/images/brokerage/manage-brokerage.png",
    panel: "linear-gradient(180deg,#d9b0ea 0%,#fcdfe6 100%)",
  },
  {
    step: "06",
    title: "Invoice Payout",
    description:
      "Streamline your brokerage operations with smart tools for tracking, managing, and optimizing deals efficiently.",
    image: "/images/brokerage/invoice-payout.png",
    panel: "linear-gradient(180deg,#fce0e6 0%,#d5a8e8 100%)",
  },
];

/* -------------------------------------------------------------------------- */
/*                           B2B SOFTWARE FEATURES                            */
/* -------------------------------------------------------------------------- */

export type BrokerageFeature = {
  id: string;
  navLabel: string;
  heading: string;
  description: string;
  points: string[];
  /** Artwork for this tab — put the file in public/images/brokerage/ */
  image: string;
  alt: string;
  /** Which side the artwork sits on, on large screens. */
  imageSide: "left" | "right";
};

export const brokerageFeatures: BrokerageFeature[] = [
  {
    id: "order-management",
    navLabel: "Order Management",
    heading: "Streamline Your Orders",
    description:
      "Effortlessly manage all transactions in one place, whether from WhatsApp, phone calls, emails, or offline sources.",
    points: [
      "Centralized Order Management",
      "Automated Notifications & Alerts",
      "Multi-Channel Integration",
      "Comprehensive Order History",
      "Easy Re-Booking Options",
    ],
    image: "/images/brokerage/order-manage.png",
    alt: "Orders list screen",
    imageSide: "right",
  },
  {
    id: "private-connections",
    navLabel: "Private Connections",
    heading: "Build Private Connections with Vendors & Customers",
    description:
      "Easily connect with vendors and customers through a secure platform, ensuring private and uninterrupted communication.",
    points: [
      "Secure Messaging System",
      "Encrypted Data for Privacy Protection",
      "Private Communication Channels",
      "Contact Management for Easy Access",
    ],
    image: "/images/brokerage/private-connection.png",
    alt: "Contacts and private connections screen",
    imageSide: "left",
  },
  {
    id: "dynamic-product-list",
    navLabel: "Dynamic Product List",
    heading: "Create a Dynamic Product List",
    description: "Easily add, update, and manage product details with full flexibility.",
    points: [
      "Bulk Upload for Faster Listing",
      "Image & Video Support for Listings",
      "Dynamic Pricing Adjustments",
      "Real-Time Inventory Management",
    ],
    image: "/images/brokerage/dynamic-product.png",
    alt: "Product list screen",
    imageSide: "right",
  },
  {
    id: "custom-pricing",
    navLabel: "Custom Pricing",
    heading: "Customize Pricing for Every Customer",
    description:
      "Offer personalized pricing to strengthen relationships and implement tailored strategies.",
    points: [
      "Easily Modify Prices Anytime",
      "Automate Custom Price Adjustments",
      "Create Tier-Based Pricing Models",
      "Set Individual Pricing for Customers",
      "Offer Exclusive Discounts & Deals",
    ],
    image: "/images/brokerage/customize-price.png",
    alt: "Custom pricing screen",
    imageSide: "left",
  },
  {
    id: "exclusive-offers",
    navLabel: "Exclusive Offers",
    heading: "Personalized Offers for Specific Customers",
    description:
      "Create exclusive promotions tailored to individual customers, boosting loyalty and driving sales.",
    points: [
      "Automate Offer Distribution",
      "Set Custom Offers for Each Customer",
      "Create Time-Limited Promotions",
      "Easily Modify & Manage Offers Anytime",
    ],
    image: "/images/brokerage/specific-customers.png",
    alt: "Exclusive offers screen",
    imageSide: "right",
  },
];

/* -------------------------------------------------------------------------- */
/*                          SMART B2B APP SCREENSHOTS                         */
/* -------------------------------------------------------------------------- */

export const smartB2BScreens = [
  { src: "/images/brokerage/app-1.png", alt: "Create Invoice – orders commission list" },
  { src: "/images/brokerage/app-2.png", alt: "Create Orders – add commission" },
  { src: "/images/brokerage/app-3.png", alt: "Create Invoice – confirm commission" },
  { src: "/images/brokerage/app-4.png", alt: "Billing App – broker dashboard" },
  { src: "/images/brokerage/app-5.png", alt: "Create Orders – products and totals" },
  { src: "/images/brokerage/app-6.png", alt: "Create Orders – products and totals" },
];

/* -------------------------------------------------------------------------- */
/*                                 CASE STUDY                                 */
/* -------------------------------------------------------------------------- */

export const brokerageCaseStudy = {
  name: "Brokerage Books",
  description:
    "Brokerage Books is a powerful B2B order management platform designed to streamline brokerage operations. From order creation to invoicing and payments, our system ensures seamless transactions and enhanced business efficiency.",
  results: [
    { value: "10k+", label: "Orders Processed Monthly" },
    { value: "40%", label: "Faster Order Completion Rate" },
  ],
  platforms: ["Android", "iPhone"],
  image: "/images/brokerage/brokerage-solution.png",
};

/* -------------------------------------------------------------------------- */
/*                               WHY CHOOSE US                                */
/* -------------------------------------------------------------------------- */

export type WhyChooseItem = {
  title: string;
  description: string;
  image: string;
};

export const whyChooseItems: WhyChooseItem[] = [
  {
    title: "All-in-One Brokerage Solution",
    description:
      "Unlock 100+ powerful features for a seamless order brokerage experience.",
    image: "/images/crm/why-5.png",
  },
  {
    title: "Fast & Efficient Order Processing",
    description:
      "Automate and streamline workflows to minimize delays and maximize speed.",
    image: "/images/crm/why-2.png",
  },
  {
    title: "Trusted & Secure Transactions",
    description:
      "Robust security measures ensure safe and reliable order handling.",
    image: "/images/multi-vendor/images/logo/secure.svg",
  },
  {
    title: "High-Performance Brokerage Platform",
    description:
      "Experience a fast, scalable, and user-friendly system for smooth operations.",
    image: "/images/multi-vendor/images/logo/computer.png",
  },
  {
    title: "24/7 Expert Support",
    description:
      "Get dedicated assistance to keep your brokerage running without interruptions.",
    image: "/images/multi-vendor/images/logo/info-girl.svg",
  },
];
/* -------------------------------------------------------------------------- */
/*                             LARAVEL SOLUTIONS                              */
/* -------------------------------------------------------------------------- */

export type LaravelSolution = {
  title: string;
  description: string;
  benefits: string[];
  Icon: IconType;
  color: string;
};

export const laravelSolutions: LaravelSolution[] = [
  {
    title: "Laravel with Vue JS",
    description:
      "Hire Laravel experts who are well-versed with VueJs to make powerful web apps with beautiful UI designs.",
    benefits: ["Faster Page loading time", "SEO-Friendly designs", "High Performance"],
    Icon: SiVuedotjs,
    color: "text-[#41B883]",
  },
  {
    title: "Laravel with HTML",
    description:
      "Develop full-stack Laravel solutions using the benefits of Laravel + HTML by Hiring Dedicated Laravel Developers.",
    benefits: [
      "Attractive Frontend Designs",
      "Flexible & Scalable Solutions",
      "Easy Traffic Handling",
    ],
    Icon: SiHtml5,
    color: "text-[#E34F26]",
  },
  {
    title: "Laravel with React",
    description:
      "Hire Dedicated Laravel Developers to create fast-performing front-ends & Restful APIs using Laravel + React.",
    benefits: ["Friendly User Experience", "Quick Loading", "Easy to Maintain"],
    Icon: SiReact,
    color: "text-[#61DAFB]",
  },
];
